import type { ReactNode } from 'react';
import { Form } from 'antd';
import type { PublicationListOut } from '@/api/generated/schemas';
import { OrderedRelationRows } from './ordered_relation/OrderedRelationRows';
import { PublicationPicker } from './ordered_relation/PublicationPicker';
import { PublicationRowContent } from './publications/PublicationRowContent';
import { publicationIssues } from './publications/publicationFacts';
import { useLineupPublications } from './publications/useLineupPublications';
import { orderedRowIds, type OrderedRow } from './orderedRows';

/**
 * Consumer rule over the resolved publication: returns a tag when the entry
 * is unfit for that consumer's surface, `null` when it is fine. A firing
 * validator both shows its tag and marks the row as an error. `undefined`
 * means the publication is gone.
 */
export type PublicationValidator = (
  publication: PublicationListOut | undefined
) => ReactNode | null;

interface OrderedPublicationCardsFieldProps {
  /** Form.List field name, e.g. `publication_rankings`. */
  name: string;
  /** Ids already on the record, so their option labels resolve. */
  existingPublicationIds?: number[];
  /** Surface-specific rules, e.g. the hero's playable-audio requirement.
   * Deleted and non-enabled entries are flagged without them. */
  validators?: PublicationValidator[];
}

/**
 * Ordered publications as card rows: cover, linked title, authors, format
 * and moderation tags, double-click to re-pick. Owns the lookups behind the
 * live rows. Collection sibling: `OrderedCollectionCardsField`.
 */
export const OrderedPublicationCardsField = ({
  name,
  existingPublicationIds,
  validators = [],
}: OrderedPublicationCardsFieldProps) => {
  const form = Form.useFormInstance();
  // Watched, not the record, so a just-picked publication renders before
  // any save.
  const rows = Form.useWatch<Array<OrderedRow<'publication_id'>> | undefined>(
    name,
    form
  );
  // Before the store initialises there is nothing to watch; the record's own
  // ids keep the first paint from rendering every row as unresolved.
  const lookups = useLineupPublications(
    rows
      ? orderedRowIds(rows, 'publication_id')
      : (existingPublicationIds ?? [])
  );

  // A row whose lookup has not landed yet says nothing — it is not "gone".
  const unresolved = (publicationId: number) =>
    !lookups.publicationById.has(publicationId) && lookups.isLoading;

  const verdicts = (publicationId: number) =>
    unresolved(publicationId)
      ? []
      : validators
          .map((validate) =>
            validate(lookups.publicationById.get(publicationId))
          )
          .filter((verdict) => verdict != null);

  const hasIssue = (publicationId: number) =>
    !unresolved(publicationId) &&
    (publicationIssues(lookups.publicationById.get(publicationId)).length > 0 ||
      verdicts(publicationId).length > 0);

  return (
    <OrderedRelationRows
      name={name}
      valueField="publication_id"
      i18nPrefix="user_catalog.common.publications_editor"
      picker={<PublicationPicker preselectedIds={existingPublicationIds} />}
      renderRow={(publicationId) => {
        const fired = verdicts(publicationId);
        return (
          <PublicationRowContent
            publicationId={publicationId}
            lookups={lookups}
            // Empty would still be truthy, and the row would draw a divider
            // with nothing after it.
            extra={
              fired.length > 0
                ? fired.map((verdict, index) => (
                    <span key={index}>{verdict}</span>
                  ))
                : undefined
            }
          />
        );
      }}
      rowError={hasIssue}
    />
  );
};
